import { FC, ReactNode } from 'react';
import { Box, Stack } from '@mui/material';
import { UserEventModel } from '@/lib/models/user-event/user-event.model';
import Text from '@/components/text';
import NoneChip from '@/components/chip/none-chip';
import AccordionButton from '@/components/accordion-button';
import RiskChip from '@/components/chip/risk-chip';
import { EvaluatedLevel } from '@/lib/models/user-event';

interface Props {
  userEvent: UserEventModel;
}

const RiskRow: FC<{ label: string; level?: EvaluatedLevel; ChipEl?: ReactNode }> = ({
  label,
  level,
  ChipEl,
}) => {
  const hasLevel = !!level;
  const hasChip = !!ChipEl;

  return (
    <Stack direction="row" justifyContent="space-between">
      <Text>{label}</Text>
      {hasChip && ChipEl}
      {!hasChip && (
        <>
          {hasLevel && <RiskChip value={level} size="small" />}
          {!hasLevel && <NoneChip notAvailable />}
        </>
      )}
    </Stack>
  );
};

const RiskCard: FC<Props> = ({ userEvent }) => {
  const { riskAssessment } = userEvent;

  if (!riskAssessment) {
    return (
      <Box>
        <NoneChip notAvailable />
      </Box>
    );
  }

  const { input, output } = riskAssessment;

  const hasInputAssessment = !!input?.overallRiskLevel;
  const InputTitleEl = (
    <Stack direction="row" justifyContent="space-between" width="100%">
      <Text bold>Input Analysis</Text>
      {hasInputAssessment ? (
        <RiskChip value={input.overallRiskLevel} size="small" />
      ) : (
        <NoneChip notAvailable />
      )}
    </Stack>
  );

  const hasOutputAssessment = !!output?.overallRiskLevel;
  const OutputTitleEl = (
    <Stack direction="row" justifyContent="space-between" width="100%">
      <Text bold>Output Analysis</Text>
      {hasOutputAssessment ? (
        <RiskChip value={output.overallRiskLevel} size="small" />
      ) : (
        <NoneChip notAvailable />
      )}
    </Stack>
  );

  return (
    <Box>
      {hasInputAssessment && (
        <Box mb={1}>
          <AccordionButton title={InputTitleEl}>
            <Stack gap={2.5} mt={2}>
              <RiskRow label="Intent Risk" level={input.intentRisk} />
              <RiskRow label="Data Sensitivity" level={input.dataSensitivity} />
              <RiskRow label="Data Exposure Impact" level={input.dataExposureImpact} />
              <RiskRow label="Harmful Content Risk" level={input.harmfulContentRisk} />
              <RiskRow label="Prompt Injection Risk" level={input.promptInjectionRisk} />
              <RiskRow label="Service Risk" level={input.serviceRisk} />
            </Stack>
          </AccordionButton>
        </Box>
      )}
      {hasOutputAssessment && (
        <AccordionButton title={OutputTitleEl}>
          <Stack gap={2.5} mt={2}>
            <RiskRow label="Harmful Content Risk" level={output.harmfulContentRisk} />
            <RiskRow label="Bias and Fairness Risk" level={output.biasRisk} />
            <RiskRow label="Service Risk" level={output.serviceRisk} />
          </Stack>
        </AccordionButton>
      )}
    </Box>
  );
};

export default RiskCard;
